<script lang="ts">
  import { get } from "svelte/store";
  import { currentTab, type TabId } from "../lib/nav";
  import { haptic } from "../lib/haptics";

  const tabs: { id: TabId; label: string }[] = [
    { id: "swim",    label: "Swim" },
    { id: "sets",    label: "Sets" },
    { id: "strokes", label: "Strokes" },
    { id: "club",    label: "Club" },
  ];

  function select(id: TabId) {
    if (get(currentTab) === id) return;
    haptic("selection");
    currentTab.set(id);
  }
</script>

<nav class="dock" aria-label="Primary navigation">
  <div class="pill" role="tablist">
    {#each tabs as t (t.id)}
      <button
        type="button"
        class="seg"
        class:active={$currentTab === t.id}
        role="tab"
        aria-selected={$currentTab === t.id}
        aria-controls={`panel-${t.id}`}
        onclick={() => select(t.id)}
      >
        <span class="icon" aria-hidden="true">
          {#if t.id === "swim"}
            <!-- swimmer arc + waterline -->
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="17.5" cy="6.5" r="1.6" fill="currentColor" stroke="none"/>
              <path d="M3 11c2.4 1.6 4.2 1.4 6 0 1.8-1.4 3.6-1.6 6-0.4l3 1.4"/>
              <path d="M3 16c2.4 1.6 4.2 1.4 6 0 1.8-1.4 3.6-1.6 6-0.4l3 1.4"/>
            </svg>
          {:else if t.id === "sets"}
            <!-- sets: one bar per swim of a set, over a baseline -->
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 18v-7M10 18V7M14 18v-5M18 18V9"/>
              <path d="M3 21h18" stroke-width="1" opacity="0.4"/>
            </svg>
          {:else if t.id === "strokes"}
            <!-- strokes: two waves (the native tab's water.waves) -->
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 9c2.4 1.6 4.2 1.6 6 0s3.6-1.6 6 0 4.2 1.6 6 0"/>
              <path d="M3 15c2.4 1.6 4.2 1.6 6 0s3.6-1.6 6 0 4.2 1.6 6 0"/>
            </svg>
          {:else}
            <!-- club: connected swimmers -->
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="8" cy="9" r="2.6"/>
              <circle cx="17" cy="10.5" r="2.2"/>
              <path d="M3 18c.8-2.6 2.6-4 5-4s4.2 1.4 5 4"/>
              <path d="M14 17c.6-1.8 2-2.8 3-2.8s2.4 1 3 2.8"/>
            </svg>
          {/if}
        </span>
        <span class="label">{t.label}</span>
      </button>
    {/each}
  </div>
</nav>

<style>
  .dock {
    position: fixed;
    left: 0;
    right: 0;
    bottom: calc(var(--space-3) + env(safe-area-inset-bottom, 0px));
    display: flex;
    justify-content: center;
    pointer-events: none;
    z-index: 30;
  }
  .pill {
    pointer-events: auto;
    display: flex;
    gap: var(--space-1);
    background: var(--bg-raised);
    border: 1px solid var(--divider);
    border-radius: var(--radius-pill);
    padding: var(--space-1);
    box-shadow: var(--shadow-floating);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  .seg {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 72px;
    padding: var(--space-2) var(--space-3);
    background: transparent;
    border: 0;
    border-radius: var(--radius-pill);
    color: var(--text-muted);
    font: var(--type-caption);
    text-transform: none;
    letter-spacing: 0;
    cursor: pointer;
    transition: color var(--dur-micro) var(--ease-swim),
                background-color var(--dur-micro) var(--ease-swim);
    -webkit-tap-highlight-color: transparent;
  }
  .seg:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }
  .seg.active {
    background: var(--bg-highest);
    color: var(--text-primary);
  }
  .icon {
    display: inline-flex;
  }
  .label {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.02em;
  }
</style>
